import axios from "axios";

// Create axios instance with base configuration
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "/api",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("doctor_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      console.error("Authentication Error (401) from:", error.config.url);
      // Handle unauthorized access
      localStorage.removeItem("doctor_token");

      // ONLY redirect if we are NOT on an admin page
      if (!window.location.pathname.startsWith("/admin")) {
        window.location.href = "/login";
      }
    }
  },
);

// Admin axios instance
export const adminApi = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "/api",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Admin request interceptor
adminApi.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem("admin_token") ||
      sessionStorage.getItem("admin_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Admin response interceptor
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      console.error("Admin Authentication Error (401):", error.config.url);
      // Note: We don't redirect here directly to avoid race conditions with Pinia state
      // The store or router should handle the logout/redirect
    }
    return Promise.reject(error);
  },
);

// Certification API instance for SERIP certification endpoints
export const certificationApi = axios.create({
  baseURL: (import.meta.env.VITE_API_URL || "/api") + "/certification",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Certification request interceptor - uses admin token for admin endpoints
certificationApi.interceptors.request.use(
  (config) => {
    // Check if this is an admin endpoint
    if (config.url.includes('/admin/')) {
      const token =
        localStorage.getItem("admin_token") ||
        sessionStorage.getItem("admin_token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Certification response interceptor
certificationApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      console.error("Certification API Authentication Error (401):", error.config.url);
    }
    return Promise.reject(error);
  },
);

// Doctor API endpoints
export const doctorAPI = {
  // Profile endpoints
  getProfile: () => api.get("/doctor/profile"),
  updateProfile: (data) => api.put("/doctor/profile", data),
  uploadAvatar: (formData) =>
    api.post("/doctor/profile/avatar", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  // Dashboard stats
  getDashboardStats: (params = {}) =>
    api.get("/doctor/dashboard/stats", { params }),
  getReportsData: (period = "30d", page = 1, per_page = 20) =>
    api.get(
      `/doctor/reports?period=${period}&page=${page}&per_page=${per_page}`,
    ),

  // Appointments endpoints
  getAppointments: (params = {}) => api.get("/doctor/appointments", { params }),
  getUpcomingAppointments: (params = {}) =>
    api.get("/doctor/appointments/upcoming", { params }),
  getRecentAppointments: (params = {}) =>
    api.get("/doctor/appointments/recent", { params }),
  updateAppointmentStatus: (id, status) =>
    api.put(`/doctor/appointments/${id}/status`, { status }),
  acceptAppointment: (id) => api.put(`/doctor/appointments/${id}/accept`),
  declineAppointment: (id, data) =>
    api.put(`/doctor/appointments/${id}/decline`, data),
  rescheduleAppointment: (id, data) =>
    api.put(`/doctor/appointments/${id}/reschedule`, data),

  // Patients endpoints
  getPatients: (params = {}) => api.get("/doctor/patients", { params }),
  getPatientDetails: (id) => api.get(`/doctor/patients/${id}`),
  getPatientHistory: (id) => api.get(`/doctor/patients/${id}/history`),
  updatePatientNotes: (id, notes) =>
    api.put(`/doctor/patients/${id}/notes`, { notes }),

  // Messages endpoints
  getConversations: (params = {}) =>
    api.get("/doctor/messages/conversations", { params }),
  getMessages: (conversationId) =>
    api.get(`/doctor/messages/conversations/${conversationId}/messages`),
  sendMessage: (conversationId, message) =>
    api.post(`/doctor/messages/conversations/${conversationId}/messages`, {
      message,
    }),
  markAsRead: (conversationId) =>
    api.put(`/doctor/messages/conversations/${conversationId}/read`),
  createConversation: (patientId) =>
    api.post("/doctor/messages/conversations", { patientId }),

  // Video/Voice Call endpoints
  getVideoToken: (conversationId, roomName = null) =>
    api.post("/doctor/video/token", {
      conversation_id: conversationId,
      room_name: roomName,
    }),
  getVoiceToken: (conversationId) =>
    api.post("/doctor/voice/token", { conversation_id: conversationId }),
  endVideoRoom: (roomSid) => api.post(`/doctor/video/room/${roomSid}/end`),

  // Billing endpoints
  getBillingOverview: (page = 1, per_page = 10, status = "all") =>
    api.get(
      `/doctor/billing/overview?page=${page}&per_page=${per_page}&status=${status}`,
    ),
  getTransactions: (params = {}) =>
    api.get("/doctor/billing/transactions", { params }),
  getRevenueData: (period = "30d") =>
    api.get(`/doctor/billing/revenue?period=${period}`),
  getPaymentMethods: () => api.get("/doctor/billing/payment-methods"),
  generateInvoice: (appointmentId) =>
    api.post(`/doctor/billing/invoices`, { appointmentId }),
  downloadInvoice: (transactionId) =>
    api.get(`/doctor/billing/transactions/${transactionId}/invoice`, {
      responseType: "blob",
    }),

  // Medical Reports endpoints
  getMedicalReports: (params = {}) =>
    api.get("/medical-reports/doctor/list", { params }),
  getMedicalReport: (id) => api.get(`/medical-reports/${id}`),
  createMedicalReport: (data) => api.post("/medical-reports/create", data),
  downloadMedicalReport: (id) =>
    api.get(`/medical-reports/${id}/download`, { responseType: "blob" }),
  viewMedicalReport: (id) =>
    api.get(`/medical-reports/${id}/view`, { responseType: "blob" }),
  emailMedicalReport: (id, data = {}) =>
    api.post(`/medical-reports/${id}/email`, data),
  deleteMedicalReport: (id) => api.delete(`/medical-reports/${id}`),
  updateMedicalReport: (id, data) => api.put(`/medical-reports/${id}`, data),

  // Additional Doctor Portal endpoints for reports view
  getDoctorPatientsList: (params = {}) =>
    api.get("/doctor/patients/list", { params }),
  getAppointmentHistory: (params = {}) =>
    api.get("/doctor/appointments/history", { params }),

  // Settings endpoints
  getSettings: () => api.get("/doctor/settings"),
  updateSettings: (data) => api.put("/doctor/settings", data),
  updateAvailability: (availability) =>
    api.put("/doctor/settings/availability", { availability }),
  updateConsultationFees: (fees) => api.put("/doctor/settings/fees", fees),

  // Feedback interactions
  likeFeedback: (id) => api.post(`/doctor/feedback/${id}/like`),
  replyToFeedback: (id, comment) =>
    api.post(`/doctor/feedback/${id}/reply`, { comment }),
};

// Utility functions for API calls
export const apiUtils = {
  // Handle API errors gracefully
  handleError: (error, fallbackMessage = "An error occurred") => {
    console.error("API Error:", error);
    return error.response?.data?.message || fallbackMessage;
  },

  // Format API response data
  formatResponse: (response) => {
    // If pagination info exists in response.data, preserve the whole object
    if (response.data?.pagination || response.data?.data?.pagination) {
      return response.data;
    }
    return response.data?.data || response.data;
  },

  // Create form data for file uploads
  createFormData: (file, additionalData = {}) => {
    const formData = new FormData();
    formData.append("file", file);
    Object.keys(additionalData).forEach((key) => {
      formData.append(key, additionalData[key]);
    });
    return formData;
  },
};

export default api;
